import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const AXES = [
    {
        key: "festivals",
        n: "01",
        kicker: "Festivals",
        title: "Les grands rituels",
        lead: "Fête du Fromage de Neufchâtel (3ᵉ week-end de septembre, Neufchâtel-en-Bray), Brocante géante de Blangy-sur-Bresle (premier dimanche du mois, 180 exposants), Journées Européennes du Patrimoine — les grands rendez-vous du territoire.",
        image: "ev_diner_chateau.jpg",
        tone: "dark",
    },
    {
        key: "concerts",
        n: "02",
        kicker: "Concerts",
        title: "Les scènes intimes",
        lead: "Rencontres Musicales Autour du Piano à Cayeux-sur-Mer, concerts dans la Collégiale d'Eu, saison musicale du Théâtre du Château d'Eu. Des jauges rares, une acoustique vraie.",
        image: "ev_concert_cliffs.jpg",
        tone: "light",
    },
    {
        key: "expos",
        n: "03",
        kicker: "Expositions",
        title: "L'œil qu'on ouvre",
        lead: "Collections permanentes du Musée Louis-Philippe (Château d'Eu), expositions temporaires du Musée du Verre de Blangy, Jardin Jungle Karlostachys à Saint-Martin-le-Gaillard, espace Neufchâtel AOP au Musée Mathon-Durand.",
        image: "ev_expo_falaises.jpg",
        tone: "light",
    },
    {
        key: "savoir-faire",
        n: "04",
        kicker: "Savoir-faire",
        title: "Les mains qui savent",
        lead: "Souffleurs de verre à Blangy (75 % du flaconnage de parfumerie mondial depuis 1506), fromagers fermiers du Neufchâtel AOP, cabines peintes de Cayeux, restaurateurs des villas Belle Époque de Mers.",
        image: "xp_atelier_parfum.jpg",
        tone: "light",
    },
    {
        key: "legendes",
        n: "05",
        kicker: "Histoire vive",
        title: "Ce qu'on se raconte",
        lead: "Louis-Philippe qui reçoit la reine Victoria au Château d'Eu en 1843, Oscar Wilde à Berneval-le-Grand pendant l'été 1897, l'abbaye cistercienne de Foucarmont fondée en 1131, les 500 villas Belle Époque de Mers classées Site Patrimonial Remarquable.",
        image: "hero_foret_eu_mist.jpg",
        tone: "dark",
    },
    {
        key: "underground",
        n: "06",
        kicker: "Côte Underground — Bientôt",
        title: "Une scène à venir",
        lead: "Lectures tardives, performances, projections, concerts en petit comité — une programmation en préparation pour les soirées qui ne s'annoncent pas sur les guides. À suivre dans la newsletter.",
        image: "pl_friche_verriers.jpg",
        tone: "underground",
    },
];

export default function SceneSection() {
    return (
        <section className="py-24 md:py-40 bg-cp-ivoire cp-grain" data-testid="scene-section">
            <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                {/* Header */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20 md:mb-28">
                    <div className="md:col-span-4">
                        <p className="cp-tiny text-cp-terre mb-5">Chapitre IX</p>
                        <h2 className="cp-serif text-[56px] md:text-[88px] lg:text-[108px] leading-[0.9] tracking-[-0.03em] text-cp-encre">
                            Scène <em className="cp-display-italic text-cp-terre">&</em><br />
                            savoir-faire.
                        </h2>
                    </div>
                    <p className="md:col-span-6 md:col-start-7 cp-serif italic text-xl md:text-3xl font-light text-cp-gris leading-[1.4] self-end">
                        Une côte ne tient pas par ses falaises — elle tient par <em>ceux qui y vivent</em>. Les festivals, les concerts, les expositions, les artisans, les légendes. Et bientôt : <em className="text-cp-terre">ce qu'on joue après minuit</em>.
                    </p>
                </div>

                {/* Axes grid — 6 cards, editorial asymmetry */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 lg:gap-14">
                    {AXES.map((axe, i) => {
                        // Alternating span for editorial asymmetry
                        const span = i % 5 === 0 || i % 5 === 3 ? "md:col-span-7" : "md:col-span-5";
                        const bg =
                            axe.tone === "dark" ? "bg-cp-encre text-cp-ivoire" :
                            axe.tone === "underground" ? "bg-cp-terre text-cp-ivoire" :
                            "bg-cp-craie text-cp-encre";
                        const kickerColor =
                            axe.tone === "underground" ? "text-cp-ivoire/80" :
                            axe.tone === "dark" ? "text-cp-sable" :
                            "text-cp-terre";
                        const offset = i % 2 === 1 ? "md:mt-16 lg:mt-24" : "";

                        return (
                            <motion.article
                                key={axe.key}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                                className={`${span} ${offset}`}
                            >
                                <Link
                                    to={axe.key === "underground" ? "#" : `/scene#${axe.key}`}
                                    data-testid={`scene-card-${axe.key}`}
                                    className={`group block ${axe.key === "underground" ? "pointer-events-none" : ""}`}
                                >
                                    <div className="cp-image-zoom relative aspect-[4/3] mb-6 overflow-hidden">
                                        <img
                                            src={`${BACKEND}/api/static/img/${axe.image}`}
                                            alt=""
                                            loading="lazy"
                                            className="w-full h-full object-cover cp-photo-cinematic"
                                        />
                                        {axe.tone === "underground" && (
                                            <div className="absolute inset-0 bg-gradient-to-br from-cp-terre/80 via-cp-encre/60 to-cp-encre/90 mix-blend-multiply" />
                                        )}
                                        {axe.tone === "underground" && (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <span className="cp-serif italic text-5xl md:text-6xl text-cp-ivoire/95">Bientôt.</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className={`${bg} px-7 md:px-9 py-7 md:py-9 border ${axe.tone === "dark" ? "border-cp-ivoire/10" : axe.tone === "underground" ? "border-cp-ivoire/20" : "border-cp-border"} transition-all group-hover:border-cp-terre/60`}>
                                        <div className="flex items-baseline gap-4 mb-4">
                                            <span className={`cp-serif italic text-3xl ${kickerColor}`}>{axe.n}</span>
                                            <span className={`cp-tiny ${kickerColor}`}>{axe.kicker}</span>
                                        </div>
                                        <h3 className="cp-serif text-3xl md:text-4xl lg:text-5xl leading-[0.95] tracking-[-0.02em] mb-5">
                                            <em className="cp-display-italic">{axe.title}</em>
                                        </h3>
                                        <p className={`cp-serif italic text-base md:text-lg leading-snug ${axe.tone === "dark" || axe.tone === "underground" ? "text-cp-ivoire/75" : "text-cp-gris"}`}>
                                            {axe.lead}
                                        </p>
                                    </div>
                                </Link>
                            </motion.article>
                        );
                    })}
                </div>

                {/* CTA */}
                <div className="mt-24 md:mt-28 border-t border-cp-border pt-10 flex items-center justify-between gap-6 flex-wrap">
                    <p className="cp-serif italic text-2xl md:text-3xl text-cp-encre max-w-xl">
                        Un territoire vivant, jamais <em>figé</em>.
                    </p>
                    <Link to="/scene" className="cp-btn-primary" data-testid="scene-all-cta">
                        Explorer la scène →
                    </Link>
                </div>
            </div>
        </section>
    );
}
